/* eslint-disable @typescript-eslint/member-ordering */
/* eslint-disable @angular-eslint/prefer-on-push-component-change-detection */

import { Component, inject, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { catchError, EMPTY, forkJoin, map, Observable, of, Subject, switchMap, take } from 'rxjs';

import { environment } from '../../../environments/environment';

import { AppService } from '../../services/app.service';
import { StoresService } from '../../services/stores.service';
import { UsersService } from '../../services/users.service';

import { ListViewComponent } from '../../components/list-view/list-view.component';

import { StoreDto } from '../../api/platform/models';
import {
  ListViewResults,
  ListViewSettings,
  ListViewTableColumnType,
} from '../../interfaces/list-view-models';
import { ServerResponse } from '../../interfaces/server-response';
import { EditStoreModalComponent } from '../../modals/edit-store-modal/edit-store-modal.component';

import { DbStoreModel } from '../../../../../shared/db-models/store';

@Component({
  selector: 'app-stores',
  standalone: false, // eslint-disable-line @angular-eslint/prefer-standalone
  templateUrl: './stores.component.html',
})
export class StoresComponent implements OnInit {
  appService: AppService;
  isLoading = true;
  storesService: StoresService;
  modalService: NgbModal;
  toastrService: ToastrService;
  usersService: UsersService;
  userNameMap: Record<string, string> = {};

  public listViewSettings: ListViewSettings = {
    allowCreate: true,
    allowEdit: true,
    allowGalleryMode: false,
    create: () => this.create(),
    createButtonText: 'Create New Store',
    defaultSortColumn: 'id',
    edit: (store: unknown) => this.edit(store as DbStoreModel),
    getData: () => this.getData(),
    hideSearch: true,
    isGalleryDefault: false,
    syncIntegrationData: (storeId: string) => this.syncIntegrationData(storeId),
    tableSettings: {
      columns: [
        {
          allowSort: false,
          columnType: ListViewTableColumnType.Text,
          displayText: 'Id',
          name: 'id',
        },
        {
          allowSort: false,
          columnType: ListViewTableColumnType.Text,
          displayText: 'Name',
          name: 'name',
        },
        {
          allowSort: false,
          columnType: ListViewTableColumnType.Text,
          displayText: 'Url',
          name: 'url',
        },
        {
          allowSort: false,
          columnType: ListViewTableColumnType.Text,
          displayText: 'Updated At',
          emptyText: '—',
          name: 'updatedAt',
          parser: (store: unknown) => {
            const ts = (store as { updatedAt?: string }).updatedAt;
            if (!ts) return '—';
            const date = new Date(ts);
            return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
          },
        },
        {
          allowSort: false,
          columnType: ListViewTableColumnType.Text,
          displayText: 'Updated By',
          emptyText: '—',
          name: 'updatedBy',
          parser: (store: unknown) => {
            const uid = (store as StoreDto).updatedBy;
            if (!uid) return '—';
            return this.userNameMap[uid] ?? uid;
          },
        },
        {
          allowSort: false,
          columnType: ListViewTableColumnType.Boolean,
          displayText: 'Is Published?',
          name: 'isPublished',
        },
      ],
      showExpand: false,
    },
  };

  constructor() {
    this.appService = inject(AppService);
    this.storesService = inject(StoresService);
    this.modalService = inject(NgbModal);
    this.toastrService = inject(ToastrService);
    this.usersService = inject(UsersService);
  }

  ngOnInit(): void {
    this.isLoading = false;
  }

  public openStoreWebsite(store: DbStoreModel): void {
    const url = `${environment.terrificWebsiteUrl}/${store.url}`;
    window.open(url, '_blank');
  }

  public deleteStore(store: StoreDto, listView: ListViewComponent): void {
    this.appService
      .showTypedConfirm(
        'Confirm action',
        `Are you sure you want to delete ${store.name}?`,
        'delete'
      )
      .pipe(
        take(1),
        switchMap((confirmed) => (confirmed ? this.storesService.deleteStore(store.id) : EMPTY))
      )
      .subscribe({
        error: (error) => {
          console.error(error);
          this.toastrService.error('Failed to delete store. Please try again.');
        },
        next: () => {
          this.toastrService.success('Store deleted successfully');
          listView.refreshData();
        },
      });
  }

  private async syncIntegrationData(storeId: string): Promise<void> {
    try {
      await this.storesService.syncIntegrationData(storeId);
      this.toastrService.success('Integration sync started successfully');
    } catch (error) {
      console.error(error);
      this.toastrService.error('An Error has occoured. Please try again.');
    }
  }

  private getData(): Observable<ServerResponse<ListViewResults<StoreDto>>> {
    return forkJoin([
      this.usersService.getAllSuperAdmins(),
      this.storesService.getAllStores(),
    ]).pipe(
      map(([users, stores]) => {
        this.userNameMap = {};
        for (const user of users) {
          this.userNameMap[user.id] = user.displayName || user.email;
        }
        return {
          data: {
            data: stores,
            dataCount: stores.length,
          },
          result: true,
        };
      }),
      catchError((err) => {
        console.error(err);
        this.toastrService.error('Failed to load stores. Please try again.');
        return of({
          data: {
            data: [],
            dataCount: 0,
          },
          result: false,
        });
      })
    );
  }

  private create(): Observable<boolean> {
    const createResult = new Subject<boolean>();

    const modal = this.modalService.open(EditStoreModalComponent, {
      ariaLabelledBy: 'modal-basic-title',
      backdrop: 'static',
      centered: true,
      size: 'lg',
      windowClass: 'dark-modal ltr',
    });

    // eslint-disable-next-line promise/catch-or-return
    modal.result
      .then(
        // eslint-disable-next-line promise/always-return
        () => {
          createResult.next(true);
        },
        () => {
          createResult.next(false);
        }
      )
      .catch(() => {
        createResult.next(false);
      })
      .finally(() => {
        createResult.complete();
      });

    return createResult;
  }

  private edit(store: DbStoreModel): Observable<boolean> {
    const createResult = new Subject<boolean>();

    const modal = this.modalService.open(EditStoreModalComponent, {
      ariaLabelledBy: 'modal-basic-title',
      backdrop: 'static',
      centered: true,
      size: 'lg',
      windowClass: 'dark-modal ltr',
    });

    (modal.componentInstance as EditStoreModalComponent).storeId = store.id;

    // eslint-disable-next-line promise/catch-or-return
    modal.result
      .then(
        () => {
          createResult.next(true);
          return true;
        },
        () => {
          createResult.next(false);
          return false;
        }
      )
      .catch(() => {
        createResult.next(false);
      })
      .finally(() => {
        createResult.complete();
      });

    return createResult;
  }
}
